import React from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import Seo from './components/Seo';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import UpcomingMatches from './pages/UpcomingMatches';
import CompletedMatches from './pages/CompletedMatches';
import MatchDetails from './pages/MatchDetails';
import LoginPage from './admin/pages/LoginPage';
import AdminDashboard from './admin/AdminPanel';
import ProtectedRoute from './admin/components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminDataProvider } from './contexts/AdminDataContext';
import MatchList from './admin/pages/matches/MatchList';
import MatchForm from './admin/pages/matches/MatchForm';
import TeamList from './admin/pages/teams/TeamList';
import TeamForm from './admin/pages/teams/TeamForm';
import PlayerList from './admin/pages/players/PlayerList';
import PlayerForm from './admin/pages/players/PlayerForm';
import LeagueList from './admin/pages/leagues/LeagueList';
import LeagueForm from './admin/pages/leagues/LeagueForm';
import TeamRoster from './admin/pages/teams/TeamRoster';
import SettleMatch from './admin/pages/matches/SettleMatch';
import PlayerDetails from './admin/pages/players/PlayerDetails';
import TeamDetails from './admin/pages/teams/TeamDetails';
import AdminMatchDetails from './admin/pages/matches/AdminMatchDetails';
import { setAuthContextGetter } from './services/apiClient';

function AppContent() {
  const location = useLocation();
  const authContext = useAuth();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  setAuthContextGetter(() => authContext);

  return (
    <HelmetProvider>
      <div className="app">
        {isAdminRoute ? (
        // Admin routes render without header/footer to prevent double scroll
        <AdminDataProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<AdminDashboard />}>
                <Route path="matches" element={<MatchList />} />
                <Route path="matches/create" element={<MatchForm />} />
                <Route path="matches/edit/:id/:date" element={<MatchForm />} />
                <Route path="matches/settle/:id/:date" element={<SettleMatch />} />
                <Route path="teams" element={<TeamList />} />
                <Route path="teams/create" element={<TeamForm />} />
                <Route path="teams/edit/:id" element={<TeamForm />} />
                <Route path="teams/:id" element={<TeamDetails />} />
                <Route path="players" element={<PlayerList />} />
                <Route path="players/create" element={<PlayerForm />} />
                <Route path="players/edit/:id" element={<PlayerForm />} />
                <Route path="players/:id" element={<PlayerDetails />} />
                <Route path="leagues" element={<LeagueList />} />
                <Route path="leagues/create" element={<LeagueForm />} />
                <Route path="leagues/edit/:id" element={<LeagueForm />} />
                <Route path="teams/roster/:id" element={<TeamRoster />} />
                <Route path="matches/settle/:id" element={<SettleMatch />} />
                <Route path="matches/:id/:date" element={<AdminMatchDetails />} />
              </Route>
            </Route>
          </Routes>
        </AdminDataProvider>
      ) : (
        // Public routes render with header/footer
        <>
          <a className="skip-link" href="#main">Skip to content</a>
          <Header />
          <main className="container" id="main">
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/upcoming-matches" element={<UpcomingMatches />} />
                <Route path="/completed-matches" element={<CompletedMatches />} />
                <Route path="/match/:id/:date" element={<MatchDetails />} />
                <Route path="/match/:id" element={<MatchDetails />} />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="*"
                  element={
                    <>
                      <Seo title="Page not found" noindex />
                      <div className="empty-state">
                        <h3>Page not found</h3>
                        <p>
                          That page doesn't exist. Try the{' '}
                          <Link to="/upcoming-matches">upcoming matches</Link> or head{' '}
                          <Link to="/">home</Link>.
                        </p>
                      </div>
                    </>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </main>
          <Footer />
        </>
      )}
    </div>
  </HelmetProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
